/* UTILS */
import type { RouteLocationNamedRaw } from 'vue-router';

import Reporter from '@/api/clients/reporter/reporter';
import { displayGenericErrorToaster, displayGenericSuccessToaster } from '@/api/helpers/helpers';
import { t } from '@/config/i18n';
/* AVATARS */
import { A_PLAYER_SCORE_MINIMUM_VALUE } from '@/enums/constants';
/* ENUMS */
import { ExperienceLevel } from '@/enums/user';
/* HELPERS */
import { capitalizeFirstLetter, decodeHtmlEntity } from '@/helpers/generic';
/* STORES */
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';

/**
 * Gets a user full name or email in absence of full name
 * @param user : user object for which to get full name
 * @return {string} fullname or email in absence of fullname
 */
export function getFullnameOrEmail(user, withEmail = true) {
  // Possibilities from what we receive from the API
  const {
    last_name: lastName = '',
    first_name: firstName = '',
    fullname: fullname, // Used in notifications
    email = ''
  } = user;

  if (fullname) {
    return decodeHtmlEntity(fullname);
  }

  if (lastName && firstName) {
    return `${capitalizeFirstLetter(decodeHtmlEntity(firstName))} ${capitalizeFirstLetter(decodeHtmlEntity(lastName))}`;
  }

  if (withEmail && email) {
    return email;
  }

  return '';
}

/**
 *  Gets a user job if it exists or return nothing
 *
 * @param {Object} user - user's data
 * @returns {String} job title to display
 */
export function getJobOrEmpty(user): string | undefined {
  // Init jobKey
  let jobKey = '';
  // Find job title key from different possibilities in user
  if (user.accurate_job_title) {
    jobKey = user.accurate_job_title;
  } else if (user.job_title) {
    jobKey = user.job_title.label;
  }
  // Return the translated key or nothing if the job occupation is no position
  return jobKey.length > 0 && jobKey !== 'data_jobs.job.no_position' ? t(jobKey) : undefined;
}

/**
 *  Gets a user job name
 *
 * @param {Object} user - user's data
 * @returns {String} job title to display
 */
export function getJobName(user) {
  // Get the job from the user query if one registerered or get nothing
  const jobQuery = getJobOrEmpty(user);

  // Return job or no job occupation
  return jobQuery || t('data_jobs.job.no_position');
}

/**
 * Get user experience level label
 *
 * @param {Number} experience - experience in years to check
 * @returns {String} Label for experience level
 */
export function getExperienceLevel(experience): string {
  // According to experience of user, retrieve the corresponding enum details
  let i = 0;
  const experiences = Object.values(ExperienceLevel);
  while (i < experiences.length && experiences[i]!.experienceRanges[0] <= experience) {
    i++;
  }
  const experienceData = Object.values(ExperienceLevel)[i - 1];

  return t(experienceData?.label ?? '');
}

/**
 * Check if user is an Aplayer
 *
 * @param {Object} user - User's Data
 * @returns {Boolean}
 */
export function isAPlayer(user) {
  return user?.aplayer_score >= A_PLAYER_SCORE_MINIMUM_VALUE;
}
export function getFallbackGradients() {
  //@TODO remove fallback once ALAvatar is fixed
  return ['#797979', '#d6d6d6'];
}
/**
 * Takes in a branding id and returns the URL for the avatar
 *
 * @param {Branding} branding - user banding
 * @param {String} format - size of image
 */
type SizeAvatar = 'small' | 'card';

export function getAvatarPath(branding, format?: SizeAvatar): string | null {
  if (!branding || !branding.id) return null;
  else {
    const profileAssetsURL = useGlobalStore().static_data_url;
    return `${profileAssetsURL}users/avatar/${format ?? 'small'}/${branding.id}.jpg`;
  }
}

/**
 * Builds the route to a candidate profile - France Travail recruiters land on their own view.
 *
 * @param {string} uuid - uuid of the candidate to open
 */
export function getProfileRoute(uuid: string): RouteLocationNamedRaw {
  const params = useAuthStore().getIsFranceTravailRecruiter ? { uuid, view: 'france-travail' } : { uuid };

  return { name: 'profile', params };
}

/**
 * Check if the connected user has a freemium or a trial plan
 *
 * @returns boolean
 */
export function isFreemiumOrTrial() {
  const company = useAuthStore().getCompany;

  return company?.plan === 'FREEMIUM' || company.is_trial;
}

export async function copyProfileUrl(uuid: string | null = null, event = 'share_profile_direct_link') {
  const url = `${window.location.origin}/recruiter/profile/${uuid}/synthesis`;

  try {
    await navigator.clipboard.writeText(url);
    displayGenericSuccessToaster(t('general.link_copied'));
    if (event) {
      Reporter().reportData(event);
    }
  } catch (err) {
    console.error(err);
    displayGenericErrorToaster();
  }
}
/**
 * Helper function to determine if the user has passed all requested tests to do affinity
 */
export function isEligibleForAffinity(user) {
  // during init, component will call this function. If no user, return false
  if (!user) {
    return false;
  }
  // Handle case where API returns a key is_shape_done instead of shape_done
  const shapeDone = !!(user.shape_done || user.is_shape_done);
  const driveDone = !!(user.drive_done || user.is_drive_done);

  return !!(shapeDone && driveDone);
}
